import React, { useState, useEffect, useMemo, useRef } from 'react';
import { LIMITES } from '../limites';
import { Zap, Star, Repeat, Target, TrendingUp, Award, CheckCircle, XCircle, Lightbulb } from 'lucide-react';
import './AdjectiveLadder.css';

// --- Configuración ---
const LADDERS_PER_GAME = 5;

function AdjectiveLadder() {
    // --- Estados del Juego ---
    const [gameState, setGameState] = useState('menu');
    const [difficulty, setDifficulty] = useState(2);
    
    const [adjectives, setAdjectives] = useState([]);
    const [currentLadderIndex, setCurrentLadderIndex] = useState(0);
    const [currentStep, setCurrentStep] = useState(0); // 0: base, 1: comparative, 2: superlative
    const [userInput, setUserInput] = useState('');
    const [feedback, setFeedback] = useState({ type: '', message: '' });
    const [showRule, setShowRule] = useState(false); // Estado para la regla

    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [correctAnswers, setCorrectAnswers] = useState(0);
    const [highScore, setHighScore] = useState(() => parseInt(localStorage.getItem('adjLadderHighScore') || '0', 10));
    
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const inputRef = useRef(null);
    const currentAdjective = useMemo(() => adjectives[currentLadderIndex], [adjectives, currentLadderIndex]);

    // Auto-focus en el input
    useEffect(() => {
        if (gameState === 'playing') {
            inputRef.current?.focus();
        }
    }, [gameState, currentStep, currentLadderIndex]);

    // --- Lógica para mostrar la regla temporalmente ---
    useEffect(() => {
        if (gameState === 'playing' && currentAdjective) {
            setShowRule(true);
            const timer = setTimeout(() => setShowRule(false), 4000); // Muestra la regla por 4 segundos
            return () => clearTimeout(timer);
        }
    }, [currentAdjective, gameState]);

    const startGame = async () => {
        setIsLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        try {
            // 🔥 CAMBIO CRÍTICO: URL RELATIVA en lugar de absoluta
            const response = await fetch('/api/minigames/get-adjective-ladder-set', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ 
                    difficulty, 
                    seen_bases: [] 
                }),
            });
            if (!response.ok) throw new Error('No se encontraron adjetivos para esta dificultad.');
            
            const data = await response.json();
            if (data.length < LADDERS_PER_GAME) throw new Error('No hay suficientes adjetivos en la base de datos para una partida completa.');

            setAdjectives(data);
            setCurrentLadderIndex(0);
            setCurrentStep(0);
            setScore(0);
            setStreak(0);
            setCorrectAnswers(0);
            setUserInput('');
            setFeedback({ type: '', message: '' });
            setGameState('playing');
        } catch (err) {
            setError(err.message);
            setGameState('menu');
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleAnswer = () => {
        if (!userInput.trim()) return;

        const isComparativeStep = currentStep === 0;
        const correctAnswer = isComparativeStep ? currentAdjective.comparative_en : currentAdjective.superlative_en;
        const isCorrect = userInput.trim().toLowerCase() === correctAnswer.toLowerCase();

        if (isCorrect) {
            const points = 10 + streak * 2;
            const newScore = score + points;
            const newCorrect = correctAnswers + 1;
            
            setScore(newScore);
            setCorrectAnswers(newCorrect);
            setStreak(s => s + 1);
            setUserInput('');
            
            if (isComparativeStep) {
                setCurrentStep(1); // Mover al siguiente escalón (superlativo)
            } else {
                // Escalera completada
                setCurrentStep(2); // Marca el último escalón como completado para que se vea la respuesta
                setTimeout(() => {
                    if (currentLadderIndex < LADDERS_PER_GAME - 1) {
                        setCurrentLadderIndex(i => i + 1);
                        setCurrentStep(0);
                    } else {
                        endGame(newScore, newCorrect);
                    }
                }, 1500); // Pausa para saborear la victoria antes de la siguiente escalera
            }
        } else {
            setStreak(0);
            setFeedback({ type: 'incorrect', message: `Incorrecto. La respuesta era: "${correctAnswer}"` });
            setTimeout(() => {
                setFeedback({ type: '', message: '' });
                if (currentLadderIndex < LADDERS_PER_GAME - 1) {
                    setCurrentLadderIndex(i => i + 1);
                    setCurrentStep(0);
                    setUserInput('');
                } else {
                    endGame(score, correctAnswers);
                }
            }, 2500);
        }
    };

    const endGame = (finalScore, finalCorrect) => {
        if (finalScore > highScore) {
            setHighScore(finalScore);
            localStorage.setItem('adjLadderHighScore', finalScore.toString());
        }

        // Guardar XP en el backend
        const saveScore = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                
                if (finalCorrect > 0) {
                    await fetch('/api/minigames/save-score', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            game_name: 'adjective_ladder',
                            score: finalScore,
                            correct_answers: finalCorrect,
                            total_questions: LADDERS_PER_GAME * 2
                        })
                    });
                }
            } catch (error) {
                console.error("Error guardando XP:", error);
            }
        };
        saveScore();

        setGameState('gameOver');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') handleAnswer();
    };

    const resetGame = () => {
        setGameState('menu');
        setError('');
    };

    // --- Renderizado de Componentes ---

    const renderMenu = () => (
        <div className="ladder-container">
            <div className="ladder-inner-container">
                <div className="ladder-menu">
                    <div className="ladder-header">
                        <TrendingUp size={40} className="color-5" />
                        <h1>Adjective Ladder</h1>
                        <p>Escala completando las formas de los adjetivos.</p>
                    </div>
                    <div className="ladder-dashboard">
                        <div><Star size={16} /><span>Points</span><strong>{highScore}</strong></div>
                        <div><Award size={16} /><span>Meta</span><strong>{LADDERS_PER_GAME} 🪜</strong></div>
                        <div><Zap size={16} /><span>Racha</span><strong>0</strong></div>
                    </div>
                    <div className="ladder-config-section">
                        <h3>Elige la Dificultad</h3>
                        <div className="ladder-difficulty-options">
                            <button onClick={() => setDifficulty(1)} className={`easy ${difficulty === 1 ? 'active' : ''}`}>Fácil</button>
                            <button onClick={() => setDifficulty(2)} className={`medium ${difficulty === 2 ? 'active' : ''}`}>Intermedio</button>
                            <button onClick={() => setDifficulty(3)} className={`hard ${difficulty === 3 ? 'active' : ''}`}>Difícil</button>
                        </div>
                    </div>
                    {error && <p className="error-message">{error}</p>}
                    <button className="ladder-play-btn" onClick={startGame} disabled={isLoading}>
                        {isLoading ? 'Cargando...' : '¡Comenzar Escalada!'}
                    </button>
                </div>
            </div>
        </div>
    );
    
    const renderGameOver = () => (
        <div className="ladder-game-over">
            <h2>¡Escalada Completada!</h2>
            <p>Tu puntaje final fue:</p>
            <strong className="final-score">{score}</strong>
            <button className="ladder-play-btn" onClick={resetGame}><Repeat size={16}/> Jugar de Nuevo</button>
        </div>
    );

    if (gameState === 'playing' || gameState === 'gameOver') {
        return (
            <div className="ladder-container">
                <div className="ladder-inner-container playing">
                    <div className="al-game-hud">
                        <div className="al-hud-item score"><Star size={16}/> {score}</div>
                        <div className="al-hud-item ladder-count"><Target size={16}/> 🪜 {currentLadderIndex + 1} / {LADDERS_PER_GAME}</div>
                        <div className="al-hud-item streak"><Zap size={16}/> x{streak}</div>
                    </div>

                    <div className="game-area">
                        {gameState === 'gameOver' ? renderGameOver() : (
                            <>
                                <div className="ladder-visual">
                                    {/* Superlative Step */}
                                    <div className={`step superlative ${currentStep >= 2 ? 'active' : ''}`}>
                                        <div className="step-content">
                                            <span>{currentStep >= 2 ? currentAdjective?.superlative_en : '???'}</span>
                                            <small>{currentAdjective?.superlative_es}</small>
                                        </div>
                                    </div>
                                    {/* Comparative Step */}
                                    <div className={`step comparative ${currentStep >= 1 ? 'active' : ''}`}>
                                        <div className="step-content">
                                            <span>{currentStep >= 1 ? currentAdjective?.comparative_en : '???'}</span>
                                            <small>{currentAdjective?.comparative_es}</small>
                                        </div>
                                    </div>
                                    {/* Base Step */}
                                    <div className="step base active">
                                        <div className="step-content">
                                            <span>{currentAdjective?.base_en}</span>
                                            <small>{currentAdjective?.base_es}</small>
                                        </div>
                                    </div>
                                </div>
                                
                                {showRule && (
                                    <div className="ladder-rule-hint">
                                        <Lightbulb size={16} />
                                        <span>{currentAdjective?.rule}</span>
                                    </div>
                                )}

                                <div className="ladder-answer-area">
                                    <p className="prompt">
                                        {currentStep === 0 ? 'Escribe la forma Comparativa' : 'Ahora, escribe la Superlativa'}
                                    </p>
                                    <input
                                        maxLength={LIMITES.respuestaMinijuego}
                                        ref={inputRef}
                                        type="text"
                                        className="answer-input"
                                        value={userInput}
                                        onChange={e => setUserInput(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Tu respuesta..."
                                        disabled={!!feedback.message || currentStep === 2}
                                    />
                                    {feedback.message && (
                                        <div className="feedback-message incorrect">
                                            <XCircle size={16} /> {feedback.message}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return renderMenu();
}

export default AdjectiveLadder;